import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { Button, type ButtonProps } from '@naf-simba/elements-buttons';
import { AdvancedImage, lazyload, placeholder, responsive } from '@cloudinary/react';
import {
  FeaturedBenefitBlockType,
  FeaturedBenefitStep,
  ColorVariant,
} from '../../../../../../types/featuredBenefitBlockType';
import { useCloudinary } from '../../../../hooks/Cloudinary/useCloudinary';
import { DynamicVideoPlayer } from '../../../video-player/DynamicVideoPlayer';
import { useDocumentUrlWithNode } from '../../../../hooks/Documents/useDocumentUrl';
import BlockContent from '../../../block-content/BlockContent';
import { Heading, StepCount, StyledButtonGroup, StyledStep } from './Styles';
import { useTrackPromotion } from '../../../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../../../utils/Tracking/Promotion/getPromotionEventTrackingData';

type ButtonsStyleSet = {
  primary: Pick<ButtonProps, 'variant' | 'color' | 'shade'>;
  secondary: Pick<ButtonProps, 'variant' | 'color' | 'shade'>;
};

export const getButtonsStyles = (color: ColorVariant): ButtonsStyleSet => {
  const buttonsStyles: ButtonsStyleSet = {
    primary: {
      color: 'secondary',
      variant: 'contained',
      shade: 'light',
    },
    secondary: {
      color: 'secondary',
      variant: 'outlined',
      shade: 'light',
    },
  };

  if (color === ColorVariant.signature || color === ColorVariant.outline) {
    buttonsStyles.primary.color = 'primary';
    buttonsStyles.primary.shade = undefined;
  }

  if (color === ColorVariant.spruce) {
    buttonsStyles.primary.color = 'secondary';
    buttonsStyles.primary.shade = 'light';
    buttonsStyles.secondary.color = 'alternative';
    buttonsStyles.secondary.shade = undefined;
  }

  if (color === ColorVariant.secondary) {
    buttonsStyles.primary.color = 'secondary';
    buttonsStyles.primary.shade = undefined;
  }

  return buttonsStyles;
};

export const ButtonGroup: React.FC<
  Pick<FeaturedBenefitBlockType, 'colorVariant' | 'primaryCta' | 'secondaryCta'> & { children?: React.ReactNode }
> = ({ primaryCta, secondaryCta, colorVariant, children }) => {
  const primaryInternalItemUrl = useDocumentUrlWithNode(primaryCta?.internalLink);
  const secondaryInternalItemUrl = useDocumentUrlWithNode(secondaryCta?.internalLink);
  const primaryPromotion = getPromotionEventTrackingData(primaryCta?.text, primaryInternalItemUrl);
  const secondaryPromotion = getPromotionEventTrackingData(secondaryCta?.text, secondaryInternalItemUrl);
  const { wrapperRef: refPrimary, trackSelectPromotion: trackPrimarySelectedPromotion } = useTrackPromotion({
    creative_slot: 'featured-benefit-block',
    creative_name: `CTA | contained-secondaryLight | ${primaryCta?.text}`,
    promotion_id: primaryPromotion?.promotion_id,
    promotion_name: primaryPromotion?.promotion_name,
  });

  const { wrapperRef: refSecondary, trackSelectPromotion: trackSecondarySelectedPromotion } = useTrackPromotion({
    creative_slot: 'featured-benefit-block',
    creative_name: `CTA | outline-secondaryLight | ${secondaryCta?.text}`,
    promotion_id: secondaryPromotion?.promotion_id,
    promotion_name: secondaryPromotion?.promotion_name,
  });

  if (!primaryCta && !secondaryCta) return null;

  const { primary, secondary } = getButtonsStyles(colorVariant);
  const shouldTrackPrimary = Boolean(primaryPromotion);
  const shouldTrackSecondary = Boolean(secondaryPromotion);

  const handlePrimaryTracking = () => {
    if (shouldTrackPrimary) {
      trackPrimarySelectedPromotion();
    }
  };

  const handleSecondaryTracking = () => {
    if (shouldTrackSecondary) {
      trackSecondarySelectedPromotion();
    }
  };

  return (
    <StyledButtonGroup $breakEarly={!!children}>
      {children}
      {primaryCta && primaryCta.externalLink && primaryCta.externalLink.href && !primaryInternalItemUrl && (
        <div ref={shouldTrackPrimary ? refPrimary : undefined}>
          <Button
            href={primaryCta.externalLink.href || ''}
            target={primaryCta.externalLink.blank ? '_blank' : '_self'}
            variant={primary.variant}
            color={primary.color}
            shade={primary.shade}
            onClick={handlePrimaryTracking}
          >
            {primaryCta.text}
          </Button>
        </div>
      )}
      {primaryCta && primaryCta.internalLink && (
        <div ref={shouldTrackPrimary ? refPrimary : undefined}>
          <Button
            type="button"
            to={primaryInternalItemUrl || ''}
            variant={primary.variant}
            color={primary.color}
            shade={primary.shade}
            onClick={handlePrimaryTracking}
          >
            {primaryCta.text}
          </Button>
        </div>
      )}
      {secondaryCta && secondaryCta.externalLink && secondaryCta.externalLink.href && !secondaryInternalItemUrl && (
        <div ref={shouldTrackSecondary ? refSecondary : undefined}>
          <Button
            href={secondaryCta.externalLink.href || ''}
            target={secondaryCta.externalLink.blank ? '_blank' : '_self'}
            variant={secondary.variant}
            color={secondary.color}
            shade={secondary.shade}
            onClick={handleSecondaryTracking}
          >
            {secondaryCta.text}
          </Button>
        </div>
      )}
      {secondaryCta && secondaryCta.internalLink && (
        <div ref={shouldTrackSecondary ? refSecondary : undefined}>
          <Button
            to={secondaryInternalItemUrl || ''}
            type="button"
            variant={secondary.variant}
            color={secondary.color}
            shade={secondary.shade}
            onClick={handleSecondaryTracking}
          >
            {secondaryCta.text}
          </Button>
        </div>
      )}
    </StyledButtonGroup>
  );
};

export const Step: React.FC<{ step: FeaturedBenefitStep; index: number; variant: ColorVariant }> = ({
  variant,
  step,
  index,
}) => (
  <StyledStep>
    <StepCount $variant={variant}>
      <Text variant={TextVariant.Header2}>{index}</Text>
    </StepCount>
    <Heading variant={TextVariant.Header2}>{step.title}</Heading>
    {/* Ensure backwards compatability as we move from string to block content. */}
    {typeof step.description === 'string' ? (
      <Text variant={TextVariant.BodyText}>{step.description}</Text>
    ) : (
      <BlockContent value={step.description} />
    )}
  </StyledStep>
);

export const MediaComponent: React.FC<Pick<FeaturedBenefitBlockType, 'image' | 'video' | 'illustration'>> = ({
  image,
  video,
  illustration,
}) => {
  const { getResponsiveImage } = useCloudinary();

  const cldImage = getResponsiveImage(image?.publicId || image?.public_id, {
    quality: 'auto:good',
    format: illustration ? 'svg' : 'auto',
  });

  if (video) {
    return (
      <DynamicVideoPlayer
        videoPlayerOptions={{ autoplay: true, autoplayMode: 'on-scroll', loop: true, muted: true }}
        video={video}
      />
    );
  }
  if (cldImage) {
    return (
      <AdvancedImage
        style={{ maxWidth: '100%' }}
        alt={image?.altText}
        cldImg={cldImage}
        plugins={[
          lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }),
          responsive({ steps: 200 }),
          placeholder({ mode: 'blur' }),
        ]}
      />
    );
  }
  return null;
};
